import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import { STATS, QUOTE_WA } from "../lib/constants";

const HERO_IMG =
  "https://images.unsplash.com/photo-1677129667171-92abd8740fa3?w=1800&q=80";

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="relative h-[58vh] min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Hotel chandelier" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#05070C] via-[#05070C]/80 to-[#05070C]/30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#05070C]/30 via-transparent to-[#05070C]" />
        </div>
        <div className="relative container-x pb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <div className="eyebrow mb-6">
              <span className="hairline" /> About Us
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.05]">
              A travel desk built on <span className="gold-text italic">discretion.</span>
            </h1>
          </motion.div>
        </div>
      </section>

      <section className="section">
        <div className="container-x grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-7">
            <SectionHeading
              eyebrow="Our Story"
              title="Born in Lilongwe. Trusted across continents."
              goldWord="continents."
            />
            <div className="mt-8 space-y-6 text-white/65 leading-relaxed">
              <p>
                Beyond Borders Travel was founded with a simple belief: that
                world-class corporate travel should not require a global
                headquarters. From our home in Lilongwe, we serve executives,
                delegations and high-end leisure clients across Africa and
                beyond — with the precision and discretion of a private office.
              </p>
              <p>
                Today, our team curates thousands of journeys a year — from
                short-haul Johannesburg shuttles to multi-leg roadshows across
                Europe, the Gulf and Asia. The standard never wavers: the right
                seat, the right room, the right car, every time.
              </p>
              <p>
                We're IATA-accredited, partnered with 800+ airlines and a
                million-plus properties, and accountable to a single promise —
                travel that quietly works. A decade of expertise, a globe of reach.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5 space-y-3">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src="https://images.pexels.com/photos/30576168/pexels-photo-30576168.jpeg"
                alt="Executive boarding aircraft"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05070C]/60 via-transparent to-transparent" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {STATS.map((s) => (
                <div
                  key={s.label}
                  data-testid={`about-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="glass p-5"
                >
                  <div className="font-display text-3xl text-white">{s.value}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.28em] text-white/55">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-[#0B1120]/40 border-y border-white/5">
        <div className="container-x">
          <SectionHeading
            eyebrow="Our values"
            title="Three principles. Quietly upheld."
            goldWord="upheld."
            align="center"
          />
          <div className="grid md:grid-cols-3 gap-px bg-white/5 mt-16 border border-white/5">
            {[
              {
                t: "Discretion",
                b: "Your travel patterns, contacts and itineraries stay between us. Always.",
              },
              {
                t: "Decisiveness",
                b: "When plans change at 03:00, we've already executed by sunrise.",
              },
              {
                t: "Detail",
                b: "Lounge access, dietary notes, driver names — the small things, never small.",
              },
            ].map((v, i) => (
              <div
                key={v.t}
                data-testid={`about-value-${i}`}
                className="bg-[#05070C] p-10"
              >
                <div className="font-display text-5xl gold-text mb-6">0{i + 1}</div>
                <h3 className="font-display text-2xl text-white mb-3">{v.t}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{v.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section text-center">
        <div className="container-x max-w-3xl mx-auto">
          <h2 className="font-display text-4xl lg:text-5xl text-white">
            Let's plan something <span className="gold-text">remarkable.</span>
          </h2>
          <a
            href={QUOTE_WA}
            target="_blank"
            rel="noreferrer"
            data-testid="about-cta-quote"
            className="btn-gold mt-10"
          >
            Speak with a consultant
            <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </section>
    </div>
  );
}
